/**
 * Resolve pdf.js once per page load, worker and all — shared by every PDF renderer
 * (`pdf-first-page.tsx`, `pdf-pages.tsx`) so the two can never load it two different ways.
 *
 * Held at module scope so opening a second PDF doesn't re-import a megabyte. Both imports are
 * dynamic, so importing THIS module costs nothing until the first PDF is opened.
 *
 * ## Why the worker is IMPORTED rather than pointed at by URL
 *
 * The documented setup is `GlobalWorkerOptions.workerSrc = <url to pdf.worker.min.mjs>`. That
 * does not work under Metro: there is no `import.meta.url`-relative asset to resolve to, so the
 * URL 404s, pdf.js logs "Setting up fake worker" and then fails outright — the page silently
 * falls back to an icon and nothing says why.
 *
 * pdf.js checks `globalThis.pdfjsWorker?.WorkerMessageHandler` BEFORE it tries to fetch
 * `workerSrc`, so importing the worker module and parking it there satisfies it with zero
 * network access and works on any bundler. Metro bundles it into the same lazy chunk, so it
 * still costs nothing until the first PDF is opened.
 *
 * The trade-off is real and accepted: this runs the worker on the MAIN THREAD rather than in a
 * real Web Worker. For one page of one document that is a brief pause, not a freeze — and it
 * beats the alternative, which is no preview at all. (A multi-page render yields between pages
 * for exactly this reason; see `pdf-pages.tsx`.)
 *
 * Importing the worker from the same package as the library also means their versions can never
 * drift, which otherwise fails with the famously unhelpful "API version does not match Worker
 * version".
 *
 * ## Why this file is `.web.ts`
 *
 * pdf.js does `await import(<variable>)`. SDK 58's Metro keeps such an import as a native
 * `import()`, which Hermes cannot compile, so an iOS RELEASE bundle fails in hermesc. PDFs are
 * only rasterised on web anyway, so native resolves the `pdfjs-loader.ts` stub instead and
 * pdf.js never enters the native bundle (`bun run check:ios-bundle` guards it).
 */
export { PDFJS_WASM_URL, pdfDocumentParams } from '@/components/documents/pdfjs-params';

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;

export const loadPdfjs = (): Promise<typeof import('pdfjs-dist')> => {
	if (!pdfjsPromise) {
		pdfjsPromise = (async () => {
			const [pdfjs, worker] = await Promise.all([
				import('pdfjs-dist'),
				import('pdfjs-dist/build/pdf.worker.mjs'),
			]);
			(globalThis as Record<string, unknown>).pdfjsWorker = worker;
			return pdfjs;
		})();
	}
	return pdfjsPromise;
};
